import { inject, Injectable, LOCALE_ID } from '@angular/core'

import { filter, find, memoize } from 'lodash-es'

import { Localizable, StaticDropDown } from '@app/components/generic/generic'

@Injectable()
export class UtilitiesService {
  public locale = inject(LOCALE_ID)

  getLookup (value: string|number, dropDownList: any[], listValueField: string = 'value', listLabelField: string = 'name'): string | number | boolean {
    // find the selected item in the list
    const option: any = find(dropDownList, function (o: any) { return o[listValueField] === value })
    // if no match was found, return empty
    if (option === undefined) return ''
    return option[listLabelField]
  }

  /**
   * Return an object from a list of objects, using the given value as a specific key for the given field.
   * For e.g. return a Country from a list of Countries, with a certain value for the `id` field
   */
  private _getLookupObject <T> (value: string|number|undefined, dropDownList: T[], listValueField: string = 'value'): T | undefined {
    // find the selected item in the list
    return find(dropDownList, function (o: any) {
      return o[listValueField] === value
    })
  }

  private _getLookupObjectResolver = (value: string|number|undefined, dropDownList: any[], listValueField: string = 'value') => {
    return JSON.stringify([value, listValueField, dropDownList])
  }

  getLookupObject = memoize(this._getLookupObject, this._getLookupObjectResolver)

  /**
   * return the locale-specific label for the given selected option of a StaticDropDown list.
   *
   * Params:
   * - value: the value of the currently selected item to be looked up, e.g. an integer id or value code
   * - dropDownList: the list of all possible options as defined in a StaticDropDown[]
   * - listValueField: optional name of the field in the StaticDropDown object which corresponds to the `value` field of the selected object above
   */
  getLocalisedLookup (value: string|number|boolean|undefined, dropDownList: Localizable[], listValueField: string = 'value'): string | undefined {
    // find the selected item in the list
    const option: Localizable|undefined = find(dropDownList, function (o: any) { return o[listValueField] === value })
    // if no match was found, return empty
    if (option === undefined) return ''
    return this.getLocalisedValue(option)
  }

  /**
   * return the locale-specific labels for the given selected options of a StaticDropDown list
   */
  getLocalisedLookups (values: string[], dropDownList: StaticDropDown[]): string[] {
    // find the selected items in the list
    const options: StaticDropDown[] = filter(dropDownList, (o) => values.find(e => e === o.value) !== undefined)
    return options.map(e => this.getLocalisedValue(e)).filter((e): e is string => e !== undefined)
  }

  /**
   * return the locale-specific label for the given StaticDropDown option
   */
  getLocalisedValue (value?: Localizable): string | undefined {
    if (this.locale === 'fr') return value?.label_fr
    return value?.label_en
  }
}
